import { Fragment, useEffect, useState } from 'react';
import {
  collection,
  getCountFromServer,
  limit,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import PageHeader from '../components/PageHeader';
import { Card } from '../components/Form';
import { formatDateTime } from '../lib/format';

interface AuditEntry {
  id: string;
  action?: string;
  [key: string]: unknown;
  adminUid?: string;
  adminEmail?: string;
  target?: Record<string, unknown>;
  outcome?: string;
  error?: string;
  createdAt?: { seconds: number };
}

const PAGE_SIZE = 100;

export default function AuditLogPage() {
  const [rows, setRows] = useState<AuditEntry[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [pageLimit, setPageLimit] = useState(PAGE_SIZE);
  const [totalCount, setTotalCount] = useState<number | null>(null);

  useEffect(() => {
    const q = query(
      collection(db, 'admin_audit_log'),
      orderBy('createdAt', 'desc'),
      limit(pageLimit)
    );
    const unsub = onSnapshot(q, (snap) => {
      setRows(
        snap.docs.map((d) => ({ ...(d.data() as AuditEntry), id: d.id }))
      );
    });
    return unsub;
  }, [pageLimit]);

  useEffect(() => {
    let active = true;
    getCountFromServer(collection(db, 'admin_audit_log'))
      .then((snap) => {
        if (active) setTotalCount(snap.data().count);
      })
      .catch(() => {
        if (active) setTotalCount(null);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <>
      <PageHeader
        title="Audit Log"
        subtitle="Append-only — entries cannot be edited or deleted."
      />
      <Card>
        <div className="mb-3 text-xs text-slate-400">
          {totalCount != null
            ? `Showing ${rows.length.toLocaleString()} of ${Math.max(
                totalCount,
                rows.length
              ).toLocaleString()}`
            : `${rows.length} shown`}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Time</th>
                <th className="px-3 py-2 text-left">Admin</th>
                <th className="px-3 py-2 text-left">Action</th>
                <th className="px-3 py-2 text-left">Outcome</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Fragment key={r.id}>
                  <tr
                    className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
                    onClick={() =>
                      setExpanded((cur) => (cur === r.id ? null : r.id))
                    }
                  >
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {formatDateTime(r.createdAt)}
                    </td>
                    <td className="px-3 py-2">{r.adminEmail || r.adminUid}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.action}</td>
                    <td className="px-3 py-2">
                      {r.error ? (
                        <span className="rounded bg-red-100 px-2 py-0.5 text-xs text-red-800">
                          error
                        </span>
                      ) : (
                        <span className="rounded bg-green-100 px-2 py-0.5 text-xs text-green-800">
                          {r.outcome || 'ok'}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-slate-400">
                      {expanded === r.id ? '▾' : '▸'}
                    </td>
                  </tr>
                  {expanded === r.id && (
                    <tr className="bg-slate-50">
                      <td colSpan={5} className="px-3 py-3">
                        <pre className="overflow-x-auto whitespace-pre-wrap break-all text-xs text-slate-700">
                          {JSON.stringify(r, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-8 text-center text-sm text-slate-500"
                  >
                    No audit entries yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {(totalCount != null
          ? rows.length < totalCount
          : rows.length >= pageLimit) && (
          <div className="mt-4 text-center">
            <button
              onClick={() => setPageLimit((n) => n + PAGE_SIZE)}
              className="rounded-md border border-slate-300 px-4 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
            >
              Load more
            </button>
          </div>
        )}
      </Card>
    </>
  );
}
